//src/modules/units/unit-sensors/views/handleunitdata.js
import global, { SPARE_AXLE, UNPAIRED_AXLE } from '../../../../shared/utils/global.js';
import { getLocationCoordinates } from './unitdiagrams/repeaters/units.api.js';
import { formatCoordinate } from '../../../../shared/utils/coordinateformats.js';
import { addLeadingZero } from '../../../../shared/utils/general.js';
import { formatZuluTime } from '../../../../shared/utils/timeformats.js';
import { fetchUnits, fetchYards, fetchTrucks, fetchTrailers, fetchTrailerLocation, fetchTruckLocation } from './unitdiagrams/repeaters/units.api.js';

const UNPAIRED_SENSOR_VISIBLE_HOURS = 24;

export const getUnits = async () => {

  if (!global.token) {
    const maxRetries = 10;  // Prevent infinite loops
    const delay = 500; // 500ms delay between checks
    let retries = 0;
    while (!localStorage.getItem("userData") && retries < maxRetries) {
      console.log(`Local storage not initialized. Retrying... (${retries + 1}/${maxRetries})`);
      await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
      retries++;
    }

    if (!localStorage.getItem("userData")) {
      console.error("Local storage variable not found after max retries. Handling failure...");
      return null; // Handle failure case properly
    }
  }

  const customerId = global.customerId;
  let [apiUnits, yards, trucks, trailers] = await Promise.all([
    fetchUnits(customerId),
    fetchYards(customerId),
    fetchTrucks(customerId),
    fetchTrailers(customerId)
  ]);

  const apiUnitsMap = new Map(apiUnits.map(unit => [unit.imeiNumber, unit]));
  const yardIMEIs = new Set(yards.map(yard => yard.IMEI));
  const truckIMEIs = new Set(trucks.map(truck => truck.IMEI));
  const trailersByTruckIMEI = new Map();

  const locationPromises = [];

  const patchUnpairedSensors = (items) =>
    (items || [])
      .filter(sensor =>
        isWithinLastHours(
          sensor?.receivedTS ?? sensor?.payloadTS,
          UNPAIRED_SENSOR_VISIBLE_HOURS
        )
      )
      .map(sensor => ({
        ...sensor,
        unpaired: true,

        // Existing UI logic expects a wheelId.
        wheelId: sensor?.wheelId || "1180",

        // Keep both forms available because older UI code uses both.
        sensorId: sensor?.sensorId ?? sensor?.Id,
        Id: sensor?.Id ?? sensor?.sensorId,

        signal: getUnpairedSensorSignalStatus(sensor),
      }));

  // Fetch trailer locations
  trailers.forEach(trailer => {
    if (trailer.Id) {
      locationPromises.push(
        fetchTrailerLocation(customerId, trailer.Id)
          .then(locationData => ({ status: 'fulfilled', trailer, locationData }))
          .catch(error => ({ status: 'rejected', trailer, error }))
      );
    }
  });

  // Fetch truck locations
  trucks.forEach(truck => {
    if (truck.Id) {
      locationPromises.push(
        fetchTruckLocation(customerId, truck.Id)
          .then(locationData => ({ status: 'fulfilled', truck, locationData }))
          .catch(error => ({ status: 'rejected', truck, error }))
      );
    }
  });

  // Wait for all location API calls to complete
  const results = await Promise.all(locationPromises);

  // Process the results
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      if (result.trailer) result.trailer.locationData = result.locationData;
      if (result.truck) result.truck.locationData = result.locationData;
    } else {
      console.warn('Location fetch failed:', result.error);
      // Optional: show error on UI or mark `trailer.error = true`, etc.
    }
  });

  trailers.forEach(trailer => {
    trailer.type = 'trailer';
    trailer.unitId = trailer.Id;
    trailer.axleRanges = trailer?.axleRanges || [];
    //trailer.signalBoosters = trailer?.signalBoosterIds || [];
    if (trailer.sensors && Array.isArray(trailer.sensors)) {
      trailer.sensors = processAxleRanges(trailer);
      trailer.sensors.forEach(sensor => {
        sensor.sensorId = sensor.Id;
      });
      trailer.wheelSensors = trailer.sensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) < SPARE_AXLE);
      trailer.spareSensors = trailer.sensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) === SPARE_AXLE);

      trailer.unitConfig = getUnitConfig(trailer);
      calculateUnitFlags(trailer);
    }

    if ((!trailer.sensors || trailer.sensors.length === 0) && trailer.repeaters) {
      trailer.wheelSensors = [];

      trailer.repeaters.forEach(repeater => {
        let { side, axle, wheel } = repeater.config;

        let wheelPositions = (side === 'Left') ? [1, 2] : [3, 4];
        if (wheel === 'Single') {
          wheelPositions = (side === 'Left') ? [1] : [4];
        }

        wheelPositions.forEach(pos => {
          trailer.wheelSensors.push({
            sensorId: `S_${trailer.unitId.slice(-3)}_${pos}${String(axle).padStart(2, '0')}${trailer.trailerNumber}`,
            wheelId: `${pos}${String(axle).padStart(2, '0')}${trailer.trailerNumber}`,
            signal: false, // Indicating no signal
            source: 'Repeater'
          });
        });
      });

      trailer.spareWheelSlots = trailer.spareWheelSlots;
      trailer.spareSensors = [];
      if (trailer.spareWheelSlots && trailer.spareWheelSlots > 0) {
        for (let i = 1; i <= trailer.spareWheelSlots; i++) {
          let wheelId = `${i}17${trailer.trailerNumber}`; // Follows the pattern 1170, 2170, etc.

          trailer.spareSensors.push({
            sensorId: `S_${trailer.unitId.slice(-3)}_SPARE_${i}`,
            wheelId: wheelId,
            signal: false, // No signal initially
            source: 'SpareWheelSlot'
          });
        }
      }
    }

    // Trailer location data update
    if (trailer.locationData) {
      trailer.timeUpdated = formatZuluTime(trailer.locationData?.recordTimestamp) || "Not available";
      trailer.speed = trailer.locationData?.speed || "0";
      trailer.location = trailer.locationData || "Not available";
    }

    if (trailer?.truckIMEI) {
      if (!trailersByTruckIMEI.has(trailer.truckIMEI)) {
        trailersByTruckIMEI.set(trailer.truckIMEI, []);
      }
      const apiUnitData = apiUnitsMap.get(trailer.truckIMEI);
      trailer.imei = trailer.truckIMEI;
      trailersByTruckIMEI.get(trailer.truckIMEI).push(trailer);
    }
  });

  let units = [];

  // Process trucks (with their linked trailers)
  trucks.forEach(truck => {
    if (truck.locationData) {
      truck.timeUpdated = formatZuluTime(truck.locationData?.recordTimestamp) || "Not available";
      truck.speed = truck.locationData?.speed || "0";
      truck.location = truck.locationData || "Not available";
    }
    const truckIMEI = truck.IMEI;
    const apiUnitData = apiUnitsMap.get(truckIMEI);
    if (!apiUnitData) return; // Ensure the API unit exists

    if (truck.sensors && Array.isArray(truck.sensors)) {
      truck.sensors = processAxleRanges(truck);
      truck.sensors.forEach(sensor => {
        sensor.sensorId = sensor.Id;
      });
    }
    let unitSensors = [...(truck.sensors || [])];
    let unitRepeaters = [...(truck.repeaters || [])];

    if ((!truck.sensors || truck.sensors.length === 0) && truck.repeaters) {
      truck.repeaters.forEach(repeater => {
        let { side, axle, wheel } = repeater.config;

        let wheelPositions = (side === 'Left') ? [1, 2] : [3, 4];
        if (wheel === 'Single') {
          wheelPositions = (side === 'Left') ? [1] : [4];
        }

        wheelPositions.forEach(pos => {
          unitSensors.push({
            sensorId: `S_${truck.Id.slice(-3)}_${pos}${String(axle).padStart(2, '0')}0`,
            wheelId: `${pos}${String(axle).padStart(2, '0')}0`,
            signal: false, // Indicating no signal
            source: 'Repeater'
          });
        });
      });

      if (truck.spareWheelSlots && truck.spareWheelSlots > 0) {
        for (let i = 1; i <= truck.spareWheelSlots; i++) {
          let wheelId = `${i}170`; // Follows the pattern 1170, 2170, etc.

          unitSensors.push({
            sensorId: `S_${truck.Id.slice(-3)}_SPARE_${i}`,
            wheelId: wheelId,
            signal: false, // No signal initially
            source: 'SpareWheelSlot'
          });
        }
      }
    }

    const linkedTrailers = trailersByTruckIMEI.get(truckIMEI) || [];
    linkedTrailers.forEach(trailer => {
      if (trailer.wheelSensors) {
        unitSensors = unitSensors.concat(trailer.wheelSensors);
      }
      if (trailer.spareSensors) {
        unitSensors = unitSensors.concat(trailer.spareSensors);
      }
      if (trailer.repeaters) {
        unitRepeaters = unitRepeaters.concat(trailer.repeaters);
      }
    });

    units.push({
      customerId: truck.customerId,
      apiUnitId: apiUnitData.Id || '',
      imei: apiUnitData.imeiNumber || '',
      deviceName: apiUnitData.deviceName || '',
      deviceType: apiUnitData.unitType || '',

      simCardNumber: apiUnitData.simCardNumber || '-',
      gatewayBatteryVoltage: apiUnitData.gateway?.batteryVoltage || '-',
      gatewayPowerSupplyVoltage: apiUnitData.gateway?.externalVoltage || '-',
      sinkId: apiUnitData.sinkId || '-',
      sinkVoltage: apiUnitData.sinkVoltage || '-',
      sinkPowerSupplyVoltage: apiUnitData.sinkPowerSupplyVoltage || '-',
      sinkReceivedTS: apiUnitData.sinkReceivedTS || '-',

      timeUpdated: truck.timeUpdated,
      speed: truck.speed,
      location: truck.location,

      notificationSettings: apiUnitData.notificationSettings || '',

      type: 'truck',

      unitType: 'TRUCK',
      unitId: truck.Id,
      name: truck.name || apiUnitData.imeiNumber,
      unitNumber: 0,
      driverName: truck.driverName,
      driverContactNumber: truck.driverContactNumber,
      officeContactNumber: truck.officeContactNumber,

      axleRanges: truck.axleRanges || [],
      axles: truck.axles,
      repeaters: unitRepeaters,
      trailers: linkedTrailers,

      signalBoosterIds: truck?.signalBoosterIds || [],

      wheelSensors: unitSensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) < SPARE_AXLE),
      spareWheelSlots: truck.spareWheelSlots,
      spareSensors: unitSensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) === SPARE_AXLE),
      unpairedSensors: patchUnpairedSensors(apiUnitData?.unpairedSensors) || [],

    });
  });

  // Process standalone trailers (trailers 'linked' to yard IMEIs)
  trailers.forEach(trailer => {
    if (!trailer?.truckIMEI) { // If it has no truck, it's standalone
      const apiUnitData = trailer?.lastReceivedIMEI
        ? apiUnitsMap.get(trailer.lastReceivedIMEI)
        : {}; // Default to an empty object to avoid errors

      const unitSensors = [...(trailer.sensors || [])];

      if ((!trailer.sensors || trailer.sensors.length === 0) && trailer.repeaters) {
        trailer.repeaters.forEach(repeater => {
          let { side, axle, wheel } = repeater.config;

          let wheelPositions = (side === 'Left') ? [1, 2] : [3, 4];
          if (wheel === 'Single') {
            wheelPositions = (side === 'Left') ? [1] : [4];
          }

          wheelPositions.forEach(pos => {
            unitSensors.push({
              sensorId: `S_${trailer.unitId.slice(-3)}_${pos}${String(axle).padStart(2, '0')}${trailer.trailerNumber}`,
              wheelId: `${pos}${String(axle).padStart(2, '0')}${trailer.trailerNumber}`,
              signal: false, // Indicating no signal
              source: 'Repeater'
            });
          });
        });
        trailer.spareWheelSlots = trailer.spareWheelSlots;
        if (trailer.spareWheelSlots && trailer.spareWheelSlots > 0) {
          for (let i = 1; i <= trailer.spareWheelSlots; i++) {
            let wheelId = `${i}17${trailer.trailerNumber}`; // Follows the pattern 1170, 2170, etc.

            unitSensors.push({
              sensorId: `S_${trailer.unitId.slice(-3)}_SPARE_${i}`,
              wheelId: wheelId,
              signal: false, // No signal initially
              source: 'SpareWheelSlot'
            });
          }
        }
      }
      units.push({
        customerId: trailer.customerId,
        apiUnitId: apiUnitData?.Id || '',
        imei: trailer.lastReceivedIMEI || apiUnitData.imeiNumber || '',

        simCardNumber: apiUnitData?.simCardNumber || '',
        deviceName: apiUnitData?.deviceName || '',
        deviceType: apiUnitData?.unitType || '',

        gatewayBatteryVoltage: apiUnitData?.gateway?.batteryVoltage || '-',
        gatewayPowerSupplyVoltage: apiUnitData?.gateway?.externalVoltage || '-',
        sinkId: apiUnitData?.sinkId || '-',
        sinkVoltage: apiUnitData?.sinkVoltage || '-',
        sinkPowerSupplyVoltage: apiUnitData?.sinkPowerSupplyVoltage || '-',
        sinkReceivedTS: apiUnitData.sinkReceivedTS || '-',

        timeUpdated: trailer.timeUpdated,
        speed: trailer.speed,
        location: trailer.location,

        notificationSettings: apiUnitData?.notificationSettings || '',

        type: 'trailer',
        unitType: 'TRAILER',
        unitId: trailer.Id,
        name: trailer.name,

        unitNumber: trailer.trailerNumber,
        trailerNumber: trailer.trailerNumber,
        trailers: [],

        axleRanges: trailer?.axleRanges || [],
        axles: trailer.axles,
        repeaters: trailer.repeaters,
        trailerSensors: trailer.sensors,
        wheelSensors: unitSensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) < SPARE_AXLE),
        spareWheelSlots: trailer.spareWheelSlots,
        spareSensors: unitSensors.filter(item => parseInt(item.wheelId?.slice(1, 3)) === SPARE_AXLE),
        unpairedSensors: patchUnpairedSensors(apiUnitData?.unpairedSensors) || []
      });
    }
  });

  // Add apiUnits that are NOT linked to a truck and are NOT a yard --- revisit???
  apiUnits.forEach(apiUnit => {
    if (!truckIMEIs.has(apiUnit.imeiNumber) && !yardIMEIs.has(apiUnit.imeiNumber)) {
      units.push({
        customerId: apiUnit.customerId,
        apiUnitId: apiUnit.Id || '',
        imei: apiUnit.imeiNumber,
        deviceName: apiUnit.deviceName,
        deviceType: apiUnit.unitType,

        gatewayBatteryVoltage: apiUnit.gateway?.batteryVoltage || '-',
        gatewayPowerSupplyVoltage: apiUnit.gateway?.externalVoltage || '-',
        sinkId: apiUnit.sinkId || '-',
        sinkVoltage: apiUnit.sinkVoltage || '-',
        sinkPowerSupplyVoltage: apiUnit.sinkPowerSupplyVoltage || '-',
        sinkReceivedTS: apiUnit.sinkReceivedTS || '-',

        timeUpdated: formatZuluTime(apiUnit.locationData?.recordTimestamp) || "Not available",
        speed: apiUnit.locationData?.speed || "0",
        location: apiUnit.locationData || "Not available",

        type: 'unassigned',
        unitType: 'UNASSIGNED',
        apiUnitId: apiUnit.Id,
        name: apiUnit.imeiNumber,
        unitNumber: 0,
        axles: [],
        repeaters: [],
        wheelSensors: [],
        spareSensors: [],
        unpairedSensors: []
      });
    }
  });

  units.forEach(unit => {
    unit.unitConfig = getUnitConfig(unit);
    calculateUnitFlags(unit);
  }
  );

  units.sort((a, b) => {
    if (b.blinkFlags !== a.blinkFlags) {
      return b.blinkFlags - a.blinkFlags;
    }
    if (b.redFlags !== a.redFlags) {
      return b.redFlags - a.redFlags;
    }
    if (b.orangeFlags !== a.orangeFlags) {
      return b.orangeFlags - a.orangeFlags;
    }
    if (b.purpleFlags !== a.purpleFlags) {
      return b.purpleFlags - a.purpleFlags;
    }
    if (b.yellowFlags !== a.yellowFlags) {
      return b.yellowFlags - a.yellowFlags;
    }
    return b.grayFlags - a.grayFlags; // Fallback to sorting by gray flags last
  });

  //const unitData = await processUnitData(units);  
  global.units = units;
  return units;
};

const processAxleRanges = (unit) => {
  let sensors = [];
  if (unit.sensors && unit.sensors.length > 0) {

    unit.sensors.forEach(sensor => {
      sensor.wheelId = sensor.tyreId || "";
      const axleNumber = sensor.wheelId?.slice(1, 3);

      // Find the corresponding axleRange by axleNumber
      const axleRange = unit.axleRanges?.find(range => range.axleNumber === axleNumber);

      // Create sensor object
      let sensorData = {
        ...sensor,
        recommendedPressure: axleRange?.recommendedPressure || 0,
        pressureDeviationAllowedLow: axleRange?.pressureDeviationAllowedLow || 0,
        pressureDeviationAllowedHigh: axleRange?.pressureDeviationAllowedHigh || 0,
        minPressure: axleRange?.minPressure || 0,
        maxPressure: axleRange?.maxPressure || 0,
        maxTemperature: axleRange?.maxTemperature || 0,
        minVoltage: 2.5, // Or axleRange?.minVoltage
        signal: getSensorSignalStatus(sensor)
      };

      sensors.push(sensorData);
    });
  }
  return sensors;
};

export async function getImeiTruck(unit) {
  let trucks = await fetchTrucks(unit.customerId);
  const truck = trucks.find(
    t => t.IMEI === unit.imei
  );
  return truck;
}

export async function getTrailer(unit) {
  let trailers = await fetchTrailers(unit.customerId);
  const trailer = trailers.find(
    t => t.Id === unit.unitId
  );
  return trailer;
}

const mergeTrailers = (linkedTrailers, trailers) => {
  // Merge linkedTrailers with trailers where name, customerId, or Id match
  const combinedTrailers = linkedTrailers.map(linkedTrailer => {
    // Find a matching trailer in trailers by Id
    const overridingTrailer = trailers.find(
      trailer =>
        trailer.Id === linkedTrailer.unitId ||
        trailer.Id === linkedTrailer.Id
    );

    return overridingTrailer
      ? { ...overridingTrailer, ...linkedTrailer } // Keep linkedTrailer data when IDs match
      : linkedTrailer; // Keep original if no match found
  });

  // Add trailers that don't exist in linkedTrailers
  const additionalTrailers = trailers.filter(trailer =>
    !linkedTrailers.some(
      linkedTrailer =>
        (linkedTrailer.Id && linkedTrailer.Id === trailer.Id) ||
        (linkedTrailer.unitId && linkedTrailer.unitId === trailer.unitId) ||
        (linkedTrailer.Id && linkedTrailer.Id === trailer.unitId) ||
        (linkedTrailer.unitId && linkedTrailer.unitId === trailer.Id)
    )
  );

  return [...combinedTrailers, ...additionalTrailers];
};

export const getTrailers = async (units) => {

  let linkedTrailers = await getLinkedTrailers();
  linkedTrailers = mapTrailerNames(linkedTrailers);

  const trailers = await fetchTrailers();

  // Combine arrays
  const result = mergeTrailers(linkedTrailers, trailers);

  return result;
};

const mapTrailerNames = (linkedTrailers) => {
  linkedTrailers.forEach((linkedTrailer) => {
    const { trailers, wheelSensors } = linkedTrailer;

    // Ensure trailers is defined and is an array
    if (Array.isArray(trailers)) {
      // Iterate over wheel sensors
      wheelSensors.forEach((sensor) => {
        // Extract the last digit of the wheelId
        const lastDigit = parseInt(sensor.wheelId.slice(-1), 10);

        // Find the matching trailer based on trailerNumber
        const matchingTrailer = trailers.find(
          (trailer) => trailer.trailerNumber === lastDigit
        );

        // If a matching trailer is found, copy its properties to the linked trailer
        if (matchingTrailer) {
          linkedTrailer.Id = matchingTrailer.Id;
          linkedTrailer.trailerNumber = matchingTrailer.trailerNumber;
          linkedTrailer.name = matchingTrailer.name;
        }
      });
    }
  });
  return linkedTrailers;
};

async function getLinkedTrailers() {

  const c = global.customerId;
  const units = await getUnits();
  const newArray = [];

  units.forEach(obj => {
    if (obj.wheelSensors && Array.isArray(obj.wheelSensors)) {
      // Find the unique last digits of all wheelIds
      const uniqueLastDigits = [...new Set(obj.wheelSensors
        .map(sensor => sensor.wheelId.slice(-1))
        .filter(digit => digit !== '0'))];

      // For each unique last digit, create a new object
      uniqueLastDigits.forEach(lastDigit => {
        // Create a copy of the object
        const newObject = { ...obj };

        // Filter the wheelSensors based on the current last digit
        newObject.wheelSensors = obj.wheelSensors.filter(sensor => sensor.wheelId.endsWith(lastDigit));
        newObject.spareSensors = obj.spareSensors.filter(sensor => sensor.wheelId.endsWith(lastDigit));

        // Remove the imei property
        //delete newObject.imei;

        // Only add the new object if there are wheelSensors that match
        if (newObject.wheelSensors.length > 0) {
          newArray.push(newObject);
        }
      });
    } else {
      // If there are no wheelSensors, simply add the original object
      const newObject = { ...obj };
      //delete newObject.imei;
      newArray.push(newObject);
    }
  });

  return newArray;
}

function isWithinLastHours(dateValue, hours = UNPAIRED_SENSOR_VISIBLE_HOURS) {
  if (!dateValue) return false;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const ageMs = Date.now() - date.getTime();
  const maxAgeMs = hours * 60 * 60 * 1000;

  return ageMs >= 0 && ageMs <= maxAgeMs;
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function hasCurrentSensorValues(sensor) {
  return (
    hasValue(sensor?.currentPressure) ||
    hasValue(sensor?.currentTemperature) ||
    hasValue(sensor?.currentVoltage)
  );
}

function getUnpairedSensorSignalStatus(sensor) {
  return (
    sensor?.status === "active" &&
    getSensorSignalStatus(sensor) &&
    hasCurrentSensorValues(sensor)
  );
}

function getSensorSignalStatus(sensor) {
  const sensorTSValue = sensor?.payloadTS || sensor?.receivedTS;

  if (!sensorTSValue) return false;

  const sensorTS = new Date(sensorTSValue);
  if (Number.isNaN(sensorTS.getTime())) return false;

  const now = new Date();

  // No Signal if older than 40 minutes
  const thresholdTime = new Date(now.getTime() - (40 * 60 * 1000));

  return sensorTS >= thresholdTime;
}

const calculateUnitFlags = (unit) => {

  unit.grayFlags = 0;
  unit.blinkFlags = 0;
  unit.redFlags = 0;
  unit.purpleFlags = 0;
  unit.orangeFlags = 0;
  unit.yellowFlags = 0;

  const sensors = [...unit.wheelSensors, ...unit.spareSensors, ...(unit.unpairedSensors || [])];
  for (let s = 0; s < sensors.length; s++) {
    if (sensors[s]?.signal) {
      if (parseFloat(sensors[s].currentPressure) > parseFloat(sensors[s].maxPressure))
        unit.purpleFlags++;
      if (sensors[s].wheelId.slice(1, 3) !== SPARE_AXLE.toString() && parseFloat(sensors[s].currentPressure) < parseFloat(sensors[s].recommendedPressure) * 20 / 100)
        unit.blinkFlags++;
      if (parseFloat(sensors[s].currentPressure) < parseFloat(sensors[s].minPressure))
        unit.redFlags++;
      if (parseFloat(sensors[s].currentTemperature) > parseFloat(sensors[s].maxTemperature))
        unit.orangeFlags++;
      if (parseFloat(sensors[s].currentVoltage) < parseFloat(sensors[s].minVoltageValue))
        unit.yellowFlags++;
    } else {
      unit.grayFlags++;
    }
  }
  return unit;
};

function parseRepeaterLocation(location) {
  const trimmed = location?.replace(/[^\w]/g, ''); // in case of 'L010' or similar
  if (!trimmed || trimmed.length < 3) return null;

  const axle = trimmed.slice(-3, -1); // middle two
  const unitNumber = trimmed.slice(-1); // last char

  return {
    axle: axle.padStart(2, '0'),
    unitNumber
  };
}

function getUniqueUnitNumbersFromRepeaters(repeaters) {
  const unitNumbers = new Set();

  for (const repeater of repeaters) {
    const parsed = parseRepeaterLocation(repeater.location);
    if (parsed) unitNumbers.add(parsed.unitNumber);
  }

  return Array.from(unitNumbers).sort();
}

function getUniqueAxlesForUnit(repeaters, unitNumber) {
  const axles = new Set();

  for (const repeater of repeaters) {
    const parsed = parseRepeaterLocation(repeater.location);
    if (parsed && parsed.unitNumber === unitNumber) {
      axles.add(parsed.axle);
    }
  }

  return Array.from(axles).sort();
}

function getUnitConfig(unit) {
  const unitConfig = [];

  const unitNumbers = getUniqueUnitNumbersFromRepeaters(unit.repeaters || []);

  for (const unitNumber of unitNumbers) {
    unitConfig.push(getUnitNumberConfigFromRepeaters(unit, unitNumber));
  }

  return unitConfig;
}

function getUnitNumberConfigFromRepeaters(unit, unitNumberStr) {
  const unitNumber = parseInt(unitNumberStr);
  const unitNumberConfig = {
    unitNumber,
    name: unitNumber === 0
      ? unit?.name || `unit ${unitNumber}`
      : (unit.trailers?.find(trailer => trailer.trailerNumber === unitNumber)?.name || `trailer ${unitNumber}`)
  };

  const axles = getAxlesFromRepeaters(unit.repeaters || [], unitNumberStr);
  unitNumberConfig.axles = axles;
  unitNumberConfig.axleCount = axles.length;
  unitNumberConfig.sensorCount = countAxleSensors(axles);

  return unitNumberConfig;
}

function getAxlesFromRepeaters(repeaters, unitNumberStr) {
  const axleNumbers = getUniqueAxlesForUnit(repeaters, unitNumberStr);

  return axleNumbers.map(axle => ({
    axle,
    wheelConfig: getWheelConfigFromRepeaters(repeaters, unitNumberStr, axle)
  }));
}

function getWheelConfigFromRepeaters(repeaters, unitNumberStr, axleStr) {
  let wheelConfig = 0; // 0 = single, 1 = double

  for (const repeater of repeaters) {
    const parsed = parseRepeaterLocation(repeater.location);
    if (!parsed) continue;

    if (parsed.unitNumber === unitNumberStr && parsed.axle === axleStr) {
      if (repeater.config?.wheel === 'Double') {
        return 1; // As soon as we find double on either side, done.
      }
    }
  }

  return wheelConfig;
}

function countAxleSensors(axles) {
  let sensorCount = 0;
  for (const axle of axles) {
    sensorCount += axle.wheelConfig === 0 ? 2 : 4;
  }
  return sensorCount;
}

export function updateUnitMetrics(unit, data) {

  const sensor = unit.wheelSensors.find(s => s.Id === data.wheelSensorUID) ||
    unit.spareSensors.find(s => s.id === data.wheelSensorUID);

  if (!sensor) {
    console.warn(`Sensor with ID ${data.wheelSensorUID} not found.`);
    return;
  }

  // Update sensor values from WebSocket payload
  sensor.signal = true;
  sensor.currentPressure = parseFloat(data.currentPressure).toFixed(2);
  sensor.currentTemperature = data.currentTemperature;
  sensor.currentVoltage = parseFloat(data.currentVoltage).toFixed(2);
  sensor.currentRotation = data.currentRotation;  // >1000 groen   

  sensor.sensorType = 'tyre-correct';
  if (Math.abs(parseInt(sensor.currentRotation)) < 1000) {
    if (parseInt(sensor.currentRotation) === -107) {
      sensor.rotationText = 'Failed';
      sensor.rotationLabelColour = 'red';
    } else {
      sensor.rotationLabelColour = 'green';
      sensor.rotationText = 'Stopped';
    }
  } else {
    sensor.rotationLabelColour = 'signalgreen';
    sensor.rotationText = 'Rolling';
  }

  if (sensor.currentPressure < sensor.minPressure) {
    sensor.pressureLabelColour = 'red';
    sensor.sensorType = 'tyre-red';
    if (sensor.wheelId.slice(1, 3) !== SPARE_AXLE && sensor.currentPressure < parseFloat(sensor.recommendedPressure) * 20 / 100) {
      sensor.sensorType = 'wheelblink';
    }
  } else if (sensor.currentPressure > sensor.maxPressure) {
    sensor.pressureLabelColour = 'purple';
    sensor.sensorType = 'tyre-purple';
  } else {
    sensor.pressureLabelColour = 'green';
  }

  if (sensor.currentTemperature > sensor.maxTemperature) {
    sensor.temperatureLabelColour = "orange";
    sensor.sensorType = "tyre-orange";
  } else {
    sensor.temperatureLabelColour = "green";
  }

  if (sensor.currentVoltage < sensor.minVoltage) {
    sensor.voltageLabelColour = "yellow";
    if (sensor.sensorType !== "tyre-purple" && sensor.sensorType !== "tyre-red" && sensor.sensorType !== "tyre-orange") {
      sensor.sensorType = "tyre-yellow";
    }
    sensors[s].voltageLabelColour = "yellow";
  } else {
    sensor.voltageLabelColour = "green";
  }

  return unit;
}

export async function getCurrentLocation(imei, timestamp) {
  try {
    const results = await getLocationCoordinates(imei, timestamp);

    if (results.data) {
      const data = results.data;
      const currentLocation = {
        speed: data.speed,
        longitude: formatCoordinate(data.longitude),
        latitude: formatCoordinate(data.latitude)
      };
      return currentLocation;
    } else {
      throw new Error('No data available');
    }
  } catch (error) {
    console.log('No location data is available!');
    throw error;
  }
}

function getAllUnitsSensorValueFlags(units) {

  for (let u = 0; u < units.length; u++) {
    let purpleflags = 0, blinkflags = 0, redflags = 0, orangeflags = 0, yellowflags = 0, blueflags = 0;
    for (let s = 0; s < units[u].sensors.length; s++) {
      if (parseFloat(units[u].sensors[s].currentPressure) > parseFloat(units[u].sensors[s].maxPressureValue))
        purpleflags++;
      if (units[u].sensors[s].sensorName.slice(0, 1) !== "9" && parseFloat(units[u].sensors[s].currentPressure) < parseFloat(units[u].sensors[s].manufacturersRecommendedPressure) * 20 / 100)
        blinkflags++;
      if (parseFloat(units[u].sensors[s].currentPressure) < parseFloat(units[u].sensors[s].minPressureValue))
        redflags++;
      if (parseFloat(units[u].sensors[s].temperatureValue) > parseFloat(units[u].sensors[s].maxTemperatureValue))
        orangeflags++;
      if (parseFloat(units[u].sensors[s].voltageValue) < parseFloat(units[u].sensors[s].minVoltageValue))
        yellowflags++;
      if (units[u].sensors[s].currentPressure == null && units[u].sensors[s].temperatureValue == null && units[u].sensors[s].voltageValue == null)
        blueflags++;
    }
    units[u].blueFlags = blueflags;
    units[u].purpleFlags = purpleflags;
    units[u].blinkFlags = blinkflags;
    units[u].redFlags = redflags;
    units[u].orangeFlags = orangeflags;
    units[u].yellowFlags = yellowflags;
  }

  return units;
}

function joinTruckAndTrailers(units) {

  let unitWithTrailers = [...units];
  let unitsToRemove = [];

  for (let i = 0; i < unitWithTrailers.length; i++) {

    unitWithTrailers[i].linkedblueFlags = unitWithTrailers[i].blueFlags;
    unitWithTrailers[i].linkedpurpleFlags = unitWithTrailers[i].purpleFlags;
    unitWithTrailers[i].linkedblinkFlags = unitWithTrailers[i].blinkFlags;
    unitWithTrailers[i].linkedredFlags = unitWithTrailers[i].redFlags;
    unitWithTrailers[i].linkedorangeFlags = unitWithTrailers[i].orangeFlags;
    unitWithTrailers[i].linkedyellowFlags = unitWithTrailers[i].yellowFlags;

    //currently only ONE trailer per unit
    if (unitWithTrailers[i].trailers.length > 0) {
      for (let j = 0; j < units.length; j++) {
        for (let t = 0; t < unitWithTrailers[i].trailers.length; t++) {
          if (unitWithTrailers[i].trailers[t].imei === units[j].imei) {

            unitWithTrailers[i].linkedblueFlags = unitWithTrailers[i].linkedblueFlags + units[j].blueFlags;
            unitWithTrailers[i].linkedpurpleFlags = unitWithTrailers[i].linkedpurpleFlags + units[j].purpleFlags;
            unitWithTrailers[i].linkedblinkFlags = unitWithTrailers[i].linkedblinkFlags + units[j].blinkFlags;
            unitWithTrailers[i].linkedredFlags = unitWithTrailers[i].linkedredFlags + units[j].redFlags;
            unitWithTrailers[i].linkedorangeFlags = unitWithTrailers[i].linkedorangeFlags + units[j].orangeFlags;
            unitWithTrailers[i].linkedyellowFlags = unitWithTrailers[i].linkedyellowFlags + units[j].yellowFlags;
            unitsToRemove.push(units[j].imei);
          }
        }
      }
    }
  }

  for (let i = 0; i < unitWithTrailers.length; i++) {
    for (let j = 0; j < unitsToRemove.length; j++) {
      if (unitWithTrailers[i] && (unitWithTrailers[i].imei === unitsToRemove[j])) {
        unitWithTrailers.splice(i, 1); i--;
      }
    }
  }

  unitWithTrailers.sort(function (a, b) {
    if (a.unitName > b.unitName) return 1;
    if (a.unitName < b.unitName) return -1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedblueFlags > b.linkedblueFlags) return -1;
    if (a.linkedblueFlags < b.linkedblueFlags) return 1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedyellowFlags > b.linkedyellowFlags) return -1;
    if (a.linkedyellowFlags < b.linkedyellowFlags) return 1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedorangeFlags > b.linkedorangeFlags) return -1;
    if (a.linkedorangeFlags < b.linkedorangeFlags) return 1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedpurpleFlags > b.linkedpurpleFlags) return -1;
    if (a.linkedpurpleFlags < b.linkedpurpleFlags) return 1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedredFlags > b.linkedredFlags) return -1;
    if (a.linkedredFlags < b.linkedredFlags) return 1;
    return 0;
  });
  unitWithTrailers.sort(function (a, b) {
    if (a.linkedblinkFlags > b.linkedblinkFlags) return -1;
    if (a.linkedblinkFlags < b.linkedblinkFlags) return 1;
    return 0;
  });

  return unitWithTrailers;
}
